import {
  IToothDetails,
  IInsurancePlan,
  PatientWithInsurance,
} from '@dataTypes/appointment.type'
import { Discount } from '@dataTypes/discount.type'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { IPhases, IPhaseItems } from '@dataTypes/treatmentPlan.types'
import { getInsuranceFieldValues } from '@utils/insurance.util'
import {
  formatToothDetailsToothLabel,
  normalizeSurfToAtomic,
} from '@utils/procedure-surface-selection.utils'
import {
  collectTxPlanMissingProviderRowKeys,
  collectTxPlanPhaseKeysMissingProcedure,
  collectTxPlanPhaseKeysWithMissingProvider,
  renumberAutoPhaseNames,
  resolveTxPlanDiscountPercent,
  txPlanDiscountFormFromPlan,
  txPlanDiscountPayloadFromForm,
  txPlanHasVisiblePhaseMissingProcedure,
} from '@utils/treatment-plan.utils'
import type {
  HasTxPlanFormChangesParams,
  ToothSurfaceEntryInput,
  TxPlanSaveProviderValidationResult,
  TxPlanToothSurfaceRecord,
} from './TreatmentPlanMain.types'

export function cloneTxPlanSnapshot<T>(value: T): T {
  try {
    return structuredClone(value)
  } catch {
    return JSON.parse(JSON.stringify(value)) as T
  }
}

export function getPhaseEditKey(
  tabKey: string,
  phase: IPhases,
  originalIdx: number,
): string {
  return `${tabKey}:${phase.phase_id || `phase-${originalIdx}`}`
}

export function escapeRegExpForPlanName(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function generateDuplicatePlanName(
  basePlanName: string,
  allPlanNames: string[],
): string {
  const base = basePlanName || 'Untitled Plan'
  const namePattern = new RegExp(
    `^${escapeRegExpForPlanName(base)}\\s*\\((\\d+)\\)$`,
  )
  const existingNumbers = allPlanNames
    .map((name) => {
      const match = name.match(namePattern)
      return match ? parseInt(match[1], 10) : null
    })
    .filter((num): num is number => num !== null)
  const nextNumber =
    existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1
  return `${base} (${nextNumber})`
}

export function generateNewTreatmentPlanName(
  allExistingNames: string[],
): string {
  const baseName = 'Treatment plan'
  const escapedBaseName = escapeRegExpForPlanName(baseName)
  const namePattern = new RegExp(`^${escapedBaseName}(?:\\s+(\\d+))?$`, 'i')
  const matchingNumbers: number[] = []
  allExistingNames.forEach((name) => {
    const trimmedName = name.trim()
    const match = trimmedName.match(namePattern)
    if (match) {
      if (match[1]) {
        const num = parseInt(match[1], 10)
        if (!isNaN(num) && num >= 0) {
          matchingNumbers.push(num)
        }
      } else {
        matchingNumbers.push(0)
      }
    }
  })
  if (matchingNumbers.length === 0) {
    return baseName
  }
  const maxNumber = Math.max(...matchingNumbers)
  return `${baseName} ${maxNumber + 1}`
}

export function calculateInsuranceForItem(
  item: IPhaseItems,
  insurance: InsurancePlan | null,
): number {
  if (!insurance || !insurance.procedures?.coinsurances) return 0
  const insuranceProcedure = insurance.procedures.coinsurances.find(
    (c) =>
      c.procedure_code === item.procedure_settings?.procedure?.procedure_code,
  )
  let insuranceAmount: number | undefined
  if (insuranceProcedure?.['in-network']) {
    const inNetworkValue = insuranceProcedure['in-network']
    if (typeof inNetworkValue === 'string' && inNetworkValue.endsWith('%')) {
      insuranceAmount = parseFloat(inNetworkValue.replace('%', ''))
    } else if (typeof inNetworkValue === 'number') {
      insuranceAmount = inNetworkValue
    } else {
      insuranceAmount = Number(inNetworkValue)
    }
  }
  return insuranceAmount ? item.charges * (insuranceAmount / 100) : 0
}

type InsurancePlanWithBenefits = (InsurancePlan | IInsurancePlan) & {
  benefits?: Array<{ procedure_id: string; co_insurance: number }>
}

type InsurancePlanWithServices = InsurancePlan & {
  services?: Array<{
    benefits?: Array<{ procedure_id: string; co_insurance: number }>
  }>
  benefits?: Array<{ procedure_id: string; co_insurance: number }>
}

function buildInsurancePlansForCalculation(
  patientInsurancePlans: PatientWithInsurance['insurance_plans'] | undefined,
  insuranceList: InsurancePlan[],
): InsurancePlanWithBenefits[] {
  let insurancePlansForCalculation: InsurancePlanWithBenefits[] = []
  if (patientInsurancePlans && patientInsurancePlans.length > 0) {
    insurancePlansForCalculation = patientInsurancePlans.map(
      (plan) =>
        ({
          ...plan,
          benefits: plan.benefits || [],
        }) as InsurancePlanWithBenefits,
    )
  }
  if (!insurancePlansForCalculation.length && insuranceList.length > 0) {
    insurancePlansForCalculation = insuranceList.map((plan) => {
      const planWithServices = plan as InsurancePlanWithServices
      if (planWithServices.services && !planWithServices.benefits) {
        const allBenefits: Array<{
          procedure_id: string
          co_insurance: number
        }> = []
        const services = planWithServices.services
        services.forEach((service) => {
          if (service.benefits) {
            allBenefits.push(...service.benefits)
          }
        })
        if (allBenefits.length > 0) {
          return {
            ...plan,
            benefits: allBenefits,
          } as InsurancePlanWithBenefits
        }
      }
      return plan
    })
  } else if (!insurancePlansForCalculation.length) {
    insurancePlansForCalculation = insuranceList
  }
  return insurancePlansForCalculation
}

export function resolveItemClinicFeeRaw(
  item: IPhaseItems,
  allowManualFeeOverride: boolean,
): number {
  if (allowManualFeeOverride && item.charges != null) {
    return item.charges
  }
  return (
    item.procedure_settings?.procedure?.procedure_settings?.clinic_fee ??
    item.procedure_settings?.clinic_fee ??
    item.charges ??
    0
  )
}

type PhaseItemFinancialParams = {
  selectedInsurance: InsurancePlan | null
  selectedDiscountId: string
  customDiscount: number
  discountsForPercent: Discount[]
  insuranceList: InsurancePlan[]
  patientInsurancePlans?: PatientWithInsurance['insurance_plans']
}

export function applyClinicFeeToPhaseItem(
  item: IPhaseItems,
  clinicFeeRaw: number,
  params: PhaseItemFinancialParams,
): IPhaseItems {
  const insurancePlansForCalculation = buildInsurancePlansForCalculation(
    params.patientInsurancePlans,
    params.insuranceList,
  )
  const insuranceValues = getInsuranceFieldValues(
    item.procedure_settings?.procedure?.procedure_id ?? '',
    clinicFeeRaw,
    params.selectedInsurance?.insurance_plan_id ?? '',
    insurancePlansForCalculation as unknown as InsurancePlan[],
  )
  const clinicFee = Number(insuranceValues.clinic_fee ?? clinicFeeRaw ?? 0)
  const allowedAmount = Number(insuranceValues.allowed_amount ?? 0)
  const writeOff = Number(insuranceValues.write_off ?? 0)
  const insuranceAmt = Number(insuranceValues.estimated_insurance_pay ?? 0)
  const estimatedPatientResp = Number(
    insuranceValues.estimated_patient_resp ?? 0,
  )
  const discountPercent = resolveTxPlanDiscountPercent(
    params.selectedDiscountId,
    params.customDiscount,
    params.discountsForPercent,
  )
  const adjustmentAmt = (estimatedPatientResp * discountPercent) / 100
  const outstandingAmt = estimatedPatientResp - adjustmentAmt
  return {
    ...item,
    charges: clinicFee,
    insurance: insuranceAmt,
    adjustments: adjustmentAmt,
    outstanding: outstandingAmt,
    allowed_amount: allowedAmount,
    write_off: writeOff,
  }
}

export function recalculatePhaseItems(
  phases: IPhases[],
  selectedInsurance: InsurancePlan | null,
  selectedDiscountId: string,
  customDiscount: number,
  discountsForPercent: Discount[],
  insuranceList: InsurancePlan[],
  patientInsurancePlans?: PatientWithInsurance['insurance_plans'],
  allowManualFeeOverride = false,
): IPhases[] {
  const financialParams: PhaseItemFinancialParams = {
    selectedInsurance,
    selectedDiscountId,
    customDiscount,
    discountsForPercent,
    insuranceList,
    patientInsurancePlans,
  }
  return phases.map((p: IPhases) => ({
    ...p,
    items: p.items.map((item: IPhaseItems) => {
      const clinicFeeRaw = resolveItemClinicFeeRaw(item, allowManualFeeOverride)
      return applyClinicFeeToPhaseItem(
        item,
        clinicFeeRaw as number,
        financialParams,
      )
    }),
  }))
}

export function renumberPhases(phasesArray: IPhases[]): IPhases[] {
  return renumberAutoPhaseNames(phasesArray)
}

function normalizePhaseScheduledDate(phase: IPhases): IPhases {
  const scheduledDate =
    phase.scheduled_date && !isNaN(Date.parse(phase.scheduled_date))
      ? new Date(phase.scheduled_date).toISOString().split('T')[0]
      : null
  return {
    ...phase,
    scheduled_date: scheduledDate,
    items: phase.items.map((item) => ({
      ...item,
    })),
  }
}

export function hasTxPlanFormChanges({
  formState,
  existingPlan,
  isLocalDraft,
  isEditMode,
  billingDiscounts,
}: HasTxPlanFormChangesParams): boolean {
  if (isLocalDraft) return true
  if (!isEditMode || !existingPlan) return false
  if (formState.planName !== (existingPlan.tp_heading || 'Plan 1')) {
    return true
  }
  const formInsuranceId = formState.insuranceProvider
    ? String(formState.insuranceProvider)
    : null
  const planInsuranceId = existingPlan.insurance_plan_id
    ? String(existingPlan.insurance_plan_id)
    : null
  if (formInsuranceId !== planInsuranceId) {
    return true
  }
  const planDiscountForm = txPlanDiscountFormFromPlan(
    existingPlan,
    billingDiscounts,
  )
  const formDiscountPayload = txPlanDiscountPayloadFromForm(
    formState.selectedDiscountId,
    formState.customDiscount,
  )
  const planDiscountPayload = txPlanDiscountPayloadFromForm(
    planDiscountForm.selectedDiscountId,
    planDiscountForm.customDiscount,
  )
  if (
    formDiscountPayload.discount_id !== planDiscountPayload.discount_id ||
    formDiscountPayload.discount !== planDiscountPayload.discount
  ) {
    return true
  }
  const formPhasesRaw = formState.phases || []
  const planPhasesRaw = existingPlan.phases || []
  if (formPhasesRaw.length !== planPhasesRaw.length) {
    return true
  }
  const formPhases = formPhasesRaw.map(normalizePhaseScheduledDate)
  const planPhases = planPhasesRaw.map((p) => {
    const scheduledDate =
      p.scheduled_date && !isNaN(Date.parse(p.scheduled_date))
        ? new Date(p.scheduled_date).toISOString().split('T')[0]
        : null
    return {
      ...p,
      scheduled_date: scheduledDate,
    }
  })
  for (let i = 0; i < formPhases.length; i++) {
    const formPhase = formPhases[i]
    const planPhase = planPhases[i]
    if (
      formPhase.phase_name !== planPhase.phase_name ||
      formPhase.status !== planPhase.status ||
      formPhase.scheduled_date !== planPhase.scheduled_date
    ) {
      return true
    }
    if (formPhase.items.length !== planPhase.items.length) {
      return true
    }
    for (let j = 0; j < formPhase.items.length; j++) {
      const formItem = formPhase.items[j]
      const planItem = planPhase.items[j]
      if (
        formItem.procedure_settings?.procedure?.procedure_code !==
          planItem.procedure_settings?.procedure?.procedure_code ||
        formItem.provider_id !== planItem.provider_id ||
        formItem.charges !== planItem.charges ||
        formItem.surface !== planItem.surface ||
        formItem.material !== planItem.material ||
        formItem.type !== planItem.type
      ) {
        return true
      }
    }
  }
  return false
}

export function groupToothSurfaceEntriesAsToothDetails(
  entries: ToothSurfaceEntryInput[],
): IToothDetails[] {
  const groupedByTooth = new Map<number, IToothDetails>()
  const toothDetails: IToothDetails[] = []
  entries.forEach((entry) => {
    const areaType = entry.area_type ?? 1
    if (areaType !== 1 || !entry.tooth) {
      if (entry.area) {
        toothDetails.push({ area_type: areaType, area: entry.area })
      }
      return
    }
    const toothNumber = Number(entry.tooth)
    if (Number.isNaN(toothNumber)) {
      return
    }
    const existingDetail = groupedByTooth.get(toothNumber)
    if (existingDetail) {
      if (entry.surf) {
        existingDetail.surf = normalizeSurfToAtomic([
          ...(existingDetail.surf ?? []),
          entry.surf,
        ])
      }
      return
    }
    const toothDetail: IToothDetails = {
      area_type: areaType,
      tooth_num: toothNumber,
    }
    if (entry.surf) {
      toothDetail.surf = normalizeSurfToAtomic([entry.surf])
    }
    groupedByTooth.set(toothNumber, toothDetail)
    toothDetails.push(toothDetail)
  })
  return toothDetails
}

export function convertToothDetailsToToothSurfaces(
  toothDetails: IToothDetails[],
  itemId: string,
): TxPlanToothSurfaceRecord[] {
  if (toothDetails.length === 0) return []
  const result: TxPlanToothSurfaceRecord[] = []
  let counter = 0
  toothDetails.forEach((detail) => {
    if (detail.area && !detail.tooth_num) {
      result.push({
        ts_id: `ts-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${counter++}`,
        item_id: itemId,
        area_type: detail.area_type ?? null,
        area: detail.area,
        tooth: null,
        surf: null,
      })
    } else if (detail.tooth_num !== undefined) {
      const toothNum = detail.tooth_num
      if (detail.surf && detail.surf.length > 0) {
        detail.surf.forEach((surface) => {
          result.push({
            ts_id: `ts-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${counter++}`,
            item_id: itemId,
            area_type: detail.area_type ?? 1,
            area: null,
            tooth: toothNum.toString(),
            surf: surface,
          })
        })
      } else {
        result.push({
          ts_id: `ts-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${counter++}`,
          item_id: itemId,
          area_type: detail.area_type ?? 1,
          area: null,
          tooth: toothNum.toString(),
          surf: null,
        })
      }
    }
  })
  return result
}

export function formatToothDetailsToString(
  toothDetails: IToothDetails[],
): string {
  if (toothDetails.length === 0) return ''
  if (toothDetails[0].area && !toothDetails[0].tooth_num) {
    return toothDetails[0].area
  }
  return toothDetails
    .map((detail) => {
      if (detail.tooth_num) {
        return formatToothDetailsToothLabel(detail)
      }
      return ''
    })
    .filter((s) => s)
    .join(', ')
}

export function createNewPhaseKey(): string {
  return `phase-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export function createEmptyPhase(
  visiblePhaseCount: number,
  phaseKey: string,
): IPhases {
  return {
    phase_name: `Phase ${visiblePhaseCount + 1}`,
    scheduled_date: null,
    status: 'Proposed',
    items: [],
    phase_id: phaseKey,
  }
}

export function duplicatePhaseAtIndex(
  phases: IPhases[],
  originalIdx: number,
  newPhaseKey: string,
): IPhases[] {
  const phaseToDuplicate = phases[originalIdx]
  const visiblePhases = phases.filter((p) => !p.is_hidden)
  return [
    ...phases,
    {
      ...phaseToDuplicate,
      phase_name: `Phase ${visiblePhases.length + 1}`,
      phase_id: newPhaseKey,
      items: phaseToDuplicate.items.map((item) => ({
        ...item,
        item_id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        phase_id: newPhaseKey,
      })),
    },
  ]
}

export function markHistoryAfterFormUpdate(
  markHistorySavePending: (tabKey: string) => void,
  isEditMode: boolean,
  isLocalDraft: boolean,
  tabKey: string,
): void {
  if (isEditMode || isLocalDraft) {
    markHistorySavePending(tabKey)
    return
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      markHistorySavePending(tabKey)
    })
  })
}

export function getTxPlanSaveProviderValidation(
  phases: IPhases[],
): TxPlanSaveProviderValidationResult {
  const hasEmptyPhases = txPlanHasVisiblePhaseMissingProcedure(phases)
  const missingProviderKeys = collectTxPlanMissingProviderRowKeys(phases)
  const hasMissingProviders = missingProviderKeys.size > 0
  const phaseValidationKeys = new Set<string>()
  if (hasEmptyPhases) {
    collectTxPlanPhaseKeysMissingProcedure(phases).forEach((key) => {
      phaseValidationKeys.add(key)
    })
  }
  if (hasMissingProviders) {
    collectTxPlanPhaseKeysWithMissingProvider(phases).forEach((key) => {
      phaseValidationKeys.add(key)
    })
  }
  return {
    hasEmptyPhases,
    hasMissingProviders,
    missingProviderKeys,
    phaseValidationKeys,
  }
}
